import { useEffect, useState, type FormEvent } from "react";
import { Heart, Trash2, CornerDownRight, Loader2, Send } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { cn } from "@/lib/cn";
import {
  type Comment,
  listComments,
  createComment,
  deleteComment,
  likeComment,
  commentLikers,
  timeAgo,
} from "@/lib/feedApi";
import { LikersDialog } from "./LikersDialog";

interface Props {
  postId: string;
  initialComments?: Comment[];
  onCountChange?: (count: number) => void;
}

// Immutable tree helpers keyed by comment id.
function mapTree(list: Comment[], id: string, fn: (c: Comment) => Comment): Comment[] {
  return list.map((c) =>
    c.id === id ? fn(c) : { ...c, replies: mapTree(c.replies, id, fn) },
  );
}
function removeFromTree(list: Comment[], id: string): Comment[] {
  return list
    .filter((c) => c.id !== id)
    .map((c) => ({ ...c, replies: removeFromTree(c.replies, id) }));
}
function countTree(list: Comment[]): number {
  return list.reduce((n, c) => n + 1 + countTree(c.replies), 0);
}

export function StoryComments({ postId, initialComments, onCountChange }: Props) {
  const [comments, setComments] = useState<Comment[] | null>(initialComments ?? null);
  const [likersFor, setLikersFor] = useState<string | null>(null);

  useEffect(() => {
    if (comments !== null) return;
    let alive = true;
    listComments(postId)
      .then((r) => alive && setComments(r.comments))
      .catch(() => alive && setComments([]));
    return () => {
      alive = false;
    };
  }, [postId, comments]);

  function applyCount(next: Comment[]) {
    setComments(next);
    onCountChange?.(countTree(next));
  }

  async function addComment(body: string, parentId: string | null) {
    const { comment } = await createComment(postId, body, parentId);
    if (comments === null) return;
    if (parentId) {
      applyCount(mapTree(comments, parentId, (c) => ({ ...c, replies: [...c.replies, comment] })));
    } else {
      applyCount([...comments, comment]);
    }
  }

  async function removeComment(id: string) {
    if (!comments) return;
    if (!window.confirm("Delete this comment?")) return;
    const next = removeFromTree(comments, id);
    applyCount(next);
    try {
      await deleteComment(postId, id);
    } catch {
      // Re-fetch on failure to resync.
      listComments(postId).then((r) => applyCount(r.comments));
    }
  }

  async function toggleLike(c: Comment) {
    if (!comments) return;
    const optimistic = mapTree(comments, c.id, (n) => ({
      ...n,
      likedByMe: !n.likedByMe,
      likeCount: n.likeCount + (n.likedByMe ? -1 : 1),
    }));
    setComments(optimistic);
    try {
      const r = await likeComment(c.id);
      setComments((cur) => (cur ? mapTree(cur, c.id, (n) => ({ ...n, likedByMe: r.liked, likeCount: r.count })) : cur));
    } catch {
      setComments(comments);
    }
  }

  if (comments === null) {
    return (
      <div className="flex items-center gap-2 px-1 py-4 text-sm text-fg-muted">
        <Loader2 className="size-4 animate-spin" /> Loading comments…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {comments.length > 0 && (
        <ul className="flex flex-col gap-3">
          {comments.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              depth={0}
              onReply={addComment}
              onDelete={removeComment}
              onLike={toggleLike}
              onShowLikers={setLikersFor}
            />
          ))}
        </ul>
      )}

      <CommentForm placeholder="Add a comment…" onSubmit={(body) => addComment(body, null)} />

      {likersFor && (
        <LikersDialog load={() => commentLikers(likersFor)} onClose={() => setLikersFor(null)} />
      )}
    </div>
  );
}

function CommentNode({
  comment,
  depth,
  onReply,
  onDelete,
  onLike,
  onShowLikers,
}: {
  comment: Comment;
  depth: number;
  onReply: (body: string, parentId: string) => Promise<void>;
  onDelete: (id: string) => void;
  onLike: (c: Comment) => void;
  onShowLikers: (id: string) => void;
}) {
  const [replying, setReplying] = useState(false);

  return (
    <li className="flex gap-2.5">
      <Avatar name={comment.userName} src={comment.userAvatarUrl} size="sm" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="rounded-[var(--radius-md)] bg-subtle px-3 py-2">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium text-fg">{comment.userName}</span>
            <span className="text-xs text-fg-subtle tabular">{timeAgo(comment.createdAt)}</span>
          </div>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-fg">{comment.body}</p>
        </div>

        <div className="mt-1 flex items-center gap-3 px-1 text-xs text-fg-muted">
          <button
            type="button"
            onClick={() => onLike(comment)}
            className={cn(
              "inline-flex items-center gap-1 hover:text-fg [&_svg]:size-3.5",
              comment.likedByMe && "text-accent-text",
            )}
          >
            <Heart className={cn(comment.likedByMe && "fill-current")} />
            {comment.likeCount > 0 ? comment.likeCount : "Like"}
          </button>
          {comment.likeCount > 0 && (
            <button type="button" onClick={() => onShowLikers(comment.id)} className="hover:text-fg">
              Who
            </button>
          )}
          {depth < 3 && (
            <button
              type="button"
              onClick={() => setReplying((r) => !r)}
              className="inline-flex items-center gap-1 hover:text-fg [&_svg]:size-3.5"
            >
              <CornerDownRight /> Reply
            </button>
          )}
          {comment.isMine && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="inline-flex items-center gap-1 hover:text-danger-fg [&_svg]:size-3.5"
            >
              <Trash2 /> Delete
            </button>
          )}
        </div>

        {replying && (
          <div className="mt-2">
            <CommentForm
              placeholder={`Reply to ${comment.userName}…`}
              autoFocus
              onSubmit={async (body) => {
                await onReply(body, comment.id);
                setReplying(false);
              }}
            />
          </div>
        )}

        {comment.replies.length > 0 && (
          <ul className="mt-3 flex flex-col gap-3 border-l border-border-subtle pl-3">
            {comment.replies.map((r) => (
              <CommentNode
                key={r.id}
                comment={r}
                depth={depth + 1}
                onReply={onReply}
                onDelete={onDelete}
                onLike={onLike}
                onShowLikers={onShowLikers}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function CommentForm({
  placeholder,
  autoFocus,
  onSubmit,
}: {
  placeholder: string;
  autoFocus?: boolean;
  onSubmit: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await onSubmit(text);
      setBody("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex items-end gap-2">
      <textarea
        autoFocus={autoFocus}
        rows={1}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
        }}
        placeholder={placeholder}
        className="min-h-9 flex-1 resize-none rounded-[var(--radius-md)] border border-border-default bg-surface px-3 py-1.5 text-sm text-fg placeholder:text-fg-subtle hover:border-border-strong focus:outline-2 focus:outline-offset-2 focus:outline-border-focus"
      />
      <button
        type="submit"
        disabled={!body.trim() || busy}
        aria-label="Send"
        className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent text-fg-on-accent hover:bg-accent-hover disabled:opacity-50 [&_svg]:size-4"
      >
        {busy ? <Loader2 className="animate-spin" /> : <Send />}
      </button>
    </form>
  );
}
